import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { TripDisplay } from "@/lib/trips";
import { useAuth } from "./AuthContext";

interface CartItem extends TripDisplay {
  quantity: number;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (trip: TripDisplay, quantity?: number) => void;
  removeFromCart: (tripId: string) => void;
  updateQuantity: (tripId: string, quantity: number) => void;
  clearCart: () => void;
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
  totalPrice: number;
  totalItems: number;
  getItemQuantity: (tripId: string) => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = "slate_us_cart";

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const { user } = useAuth();

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        if (Array.isArray(parsedCart) && parsedCart.length > 0) {
          setItems(parsedCart);
        }
      }
    } catch (error) {
      console.error("Error loading cart from localStorage:", error);
    }
  }, []);

  // Restore cart when user signs in
  useEffect(() => {
    if (user) {
      try {
        const savedCart = localStorage.getItem(CART_STORAGE_KEY);
        if (savedCart) {
          const parsedCart = JSON.parse(savedCart);
          if (Array.isArray(parsedCart) && parsedCart.length > 0) {
            setItems(parsedCart);
          }
        }
      } catch (error) {
        console.error("Error restoring cart after sign-in:", error);
      }
    }
  }, [user]);

  // Save cart to localStorage whenever items change
  useEffect(() => {
    if (items.length > 0) {
      try {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
      } catch (error) {
        console.error("Error saving cart to localStorage:", error);
      }
    } else {
      // Clear localStorage if cart is empty
      localStorage.removeItem(CART_STORAGE_KEY);
    }
  }, [items]);

  // Parse group_size to get max quantity (e.g., "10-14 travelers" -> 14)
  const getMaxQuantity = (groupSize: string): number => {
    const match = groupSize.match(/(\d+)/g);
    if (match && match.length > 0) {
      // Get the last number (usually the max)
      const max = parseInt(match[match.length - 1], 10);
      return max || 20; // Default to 20 if parsing fails
    }
    return 20; // Default max quantity
  };

  const addToCart = (trip: TripDisplay, quantity: number = 1) => {
    setItems((prev) => {
      const existing = prev.find((item) => item.id === trip.id);
      const maxQty = getMaxQuantity(trip.groupSize);
      
      if (existing) {
        const newQuantity = Math.min(existing.quantity + quantity, maxQty);
        return prev.map((item) =>
          item.id === trip.id ? { ...item, quantity: newQuantity } : item
        );
      }
      return [...prev, { ...trip, quantity: Math.min(quantity, maxQty) }];
    });
    setIsCartOpen(true);
  };

  const removeFromCart = (tripId: string) => {
    setItems((prev) => prev.filter((item) => item.id !== tripId));
  };

  const updateQuantity = (tripId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(tripId);
      return;
    }

    setItems((prev) => {
      const item = prev.find((i) => i.id === tripId);
      if (!item) return prev;

      const maxQty = getMaxQuantity(item.groupSize);
      const newQuantity = Math.min(Math.max(quantity, 1), maxQty);

      if (newQuantity <= 0) {
        return prev.filter((i) => i.id !== tripId);
      }

      return prev.map((i) =>
        i.id === tripId ? { ...i, quantity: newQuantity } : i
      );
    });
  };

  const getItemQuantity = (tripId: string): number => {
    const item = items.find((i) => i.id === tripId);
    return item?.quantity || 0;
  };

  const clearCart = () => {
    setItems([]);
    localStorage.removeItem(CART_STORAGE_KEY);
  };

  const totalPrice = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        isCartOpen,
        setIsCartOpen,
        totalPrice,
        totalItems,
        getItemQuantity,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
